import { useState, useCallback, useEffect, useRef } from "react"
import axiosInstance from "../../../services/axios-client"
import { toast } from "react-toastify"
import { debounce } from "lodash"
import { handleApiError } from "../../../utils/errorHandler"

const useProjectDetail = () => {
    // ============= STATE MANAGEMENT =============
    // Project state
    const [payload, setPayload] = useState({})
    const [loadingPagestate, setLoadingPage] = useState(false)
    const [isSendNotification, setIsSendNotification] = useState(false)

    // Rating Quota state
    const [ratingQuotas, setRatingQuota] = useState([])
    const [pageRatingQuota, setPageRatingQuota] = useState(1)
    const [totalPageRatingQuota, setTotalPageRatingQuota] = useState(1)
    const [limitRatingQuota, setLimitRatingQuota] = useState(10)
    const [filterNameRatingQuota, setFilterNameRatingQuota] = useState("")

    // Users state
    const [users, setUsers] = useState([])
    const [userCalibration, setUserCalibration] = useState([])
    const [pageUsers, setPageUsers] = useState(1)
    const [totalPageUsers, setTotalPageUsers] = useState(1)
    const [limitUsers, setLimitUsers] = useState(10)
    const [filterName, setFilterName] = useState("")
    const [filterDivision, setFilterDivision] = useState("")
    const [divisions, setDivisionsState] = useState([])

    // ============= RATING QUOTA HANDLERS =============
    const changePageRatingQuota = useCallback((page) => {
        setPageRatingQuota(page)
    }, [])

    const setFilterNameUserRatingQuota = useCallback((e) => {
        setFilterNameRatingQuota(e.target.value)
        setPageRatingQuota(1)
    }, [])

    const setFilterLimitRatingQuota = useCallback((data) => {
        setLimitRatingQuota(data)
        setPageRatingQuota(1)
    }, [])

    // ============= USER HANDLERS =============
    const changePageUsers = useCallback(async (page) => {
        setPageUsers(page)
    }, [])

    const setFilterNameUsers = useCallback((e) => {
        setFilterName(e.target.value)
        setPageUsers(1)
    }, [])

    const setFilterDivisionUsers = useCallback((e) => {
        setFilterDivision(e ?? "")
        setPageUsers(1)
    }, [])

    const setFilterLimitUsers = useCallback((data) => {
        setLimitUsers(data)
        setPageUsers(1)
    }, [])


    // ============= DATA PROCESSING =============
    const setDataCalibration = useCallback(() => {
        if (!users.length || !payload.ProjectPhases?.length) {
            setUserCalibration({})
            return
        }

        const data = {}
        const maxPhaseOrder = payload.ProjectPhases[payload.ProjectPhases.length - 1].Phase.Order

        users.forEach(user => {
            const calUser = []

            if (!user.CalibrationScores?.length) {
                // Fill with empty cells for users with no calibration scores
                for (let i = 1; i <= maxPhaseOrder; i++) {
                    calUser.push(<td key={`empty-${i}`} className="text-center">-</td>)
                }
                calUser.push(<td key="spmo-empty" className="text-center">-</td>)
            } else {
                // Process each phase
                for (let index = 1; index <= maxPhaseOrder; index++) {
                    const matchingScore = user.CalibrationScores.find(
                        score => score.ProjectPhase.Phase.Order === index
                    )

                    if (matchingScore) {
                        calUser.push(
                            <td key={`phase-${index}`} className="text-center">
                                {matchingScore.Calibrator?.Name}
                            </td>
                        )

                        // Add SPMO on last phase
                        if (index === maxPhaseOrder) {
                            calUser.push(
                                <td key="spmo" className="text-center">
                                    {matchingScore.Spmo?.Name}
                                </td>
                            )
                        }
                    } else {
                        calUser.push(<td key={`empty-${index}`} className="text-center">-</td>)
                    }
                }
            }

            data[user.ID] = calUser
        })

        setUserCalibration(data)
    }, [users, payload.ProjectPhases])

    // ============= DEBOUNCED SEARCH =============
    const debouncedGetRatingQuotaRef = useRef(
        debounce((id, page, name, limit) => {
            setLoadingPage(true)
            axiosInstance.get(
                `/auth/rating-quotas?page=${page}&limit=${limit}&name=${name}&id=${id}`
            ).then(res => {
                setRatingQuota(res.data.data)
                setTotalPageRatingQuota(res.data.paging.TotalPages)
            }).catch(error => {
                handleApiError(error)
            }).finally(() => {
                setLoadingPage(false)
            })
        }, 800)
    ).current

    const debouncedGetUserRef = useRef(
        debounce((id, page, limit, name, division) => {
            setLoadingPage(true)
            axiosInstance.get(
                `/auth/users/project/${id}?page=${page}&limit=${limit}&name=${name}&division=${encodeURIComponent(division)}`
            ).then(res => {
                setUsers(res.data.data)
                setTotalPageUsers(res.data.paging.TotalPages)
            }).catch(error => {
                handleApiError(error)
            }).finally(() => {
                setLoadingPage(false)
            })
        }, 800)
    ).current

    // ============= EFFECTS =============
    useEffect(() => {
        if (payload.ID) {
            debouncedGetRatingQuotaRef(payload.ID, pageRatingQuota, filterNameRatingQuota, limitRatingQuota)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterNameRatingQuota, payload.ID, pageRatingQuota, limitRatingQuota])

    useEffect(() => {
        if (payload.ID) {
            debouncedGetUserRef(payload.ID, pageUsers, limitUsers, filterName, filterDivision)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterName, filterDivision, payload.ID, pageUsers, limitUsers])

    // Manual fetch function for users data
    const getUsersData = useCallback(async (projectId) => {
        try {
            setLoadingPage(true)
            const res = await axiosInstance.get(
                `/auth/users/project/${projectId}?page=${pageUsers}&limit=${limitUsers}&name=${filterName}&division=${encodeURIComponent(filterDivision)}`
            )
            setUsers(res.data.data)
            setTotalPageUsers(res.data.paging.TotalPages)
        } catch (error) {
            handleApiError(error)
        } finally {
            setLoadingPage(false)
        }
    }, [pageUsers, limitUsers, filterName, filterDivision])

    // ============= API CALLS =============
    const getProjectForm = useCallback(async (id) => {
        try {
            setLoadingPage(true)
            const res = await axiosInstance.get(`/auth/projects/${id}`)
            setPayload(res.data.data)
        } catch (error) {
            handleApiError(error)
        } finally {
            setLoadingPage(false)
        }
    }, [])

    const getRatingQuota = useCallback(async (id, page, name) => {
        try {
            setLoadingPage(true)
            const res = await axiosInstance.get(
                `/auth/rating-quotas?page=${page}&limit=${limitRatingQuota}&name=${name}&id=${id}`
            )
            setRatingQuota(res.data.data)
            setTotalPageRatingQuota(res.data.paging.TotalPages)
        } catch (error) {
            handleApiError(error)
        } finally {
            setLoadingPage(false)
        }
    }, [limitRatingQuota])



    const getDivisions = useCallback(async (id) => {
        try {
            const res = await axiosInstance.get(`/auth/users/division/${id}`)
            setDivisionsState(res.data.data)
        } catch (error) {
            handleApiError(error)
        }
    }, [])

    const publishProject = useCallback(async (id) => {
        try {
            setLoadingPage(true)
            await axiosInstance.post(`/auth/projects/publish/${id}`)
            toast.success("Success Publish Project")
            setTimeout(() => window.location.reload(), 1000)
        } catch (error) {
            handleApiError(error)
        } finally {
            setLoadingPage(false)
        }
    }, [])

    const deactivateProject = useCallback(async (id) => {
        try {
            setLoadingPage(true)
            await axiosInstance.post(`/auth/projects/deactive/${id}`)
            toast.success("Success Deactivate Project")
            setTimeout(() => window.location.reload(), 1000)
        } catch (error) {
            handleApiError(error)
        } finally {
            setLoadingPage(false)
        }
    }, [])

    const notifyCalibrator = useCallback(async (projectID) => {
        try {
            setIsSendNotification(true)
            // setLoadingPage(true)
            await axiosInstance.post(`/auth/projects/send-notification-first-calibrator/${projectID}`)
            toast.success("Success Notify Calibrator")
        } catch (error) {
            handleApiError(error)
        } finally {
            // setLoadingPage(false)
            setIsSendNotification(false)
        }
    }, [])

    const deleteRatingQuota = useCallback(async (id) => {
        try {
            setLoadingPage(true)
            await axiosInstance.delete(`/auth/rating-quotas/${id}`)
            toast.success("Success Delete Rating Quota")
            // Refresh the rating quota data without page reload
            await getRatingQuota(payload.ID, pageRatingQuota, filterNameRatingQuota)
        } catch (error) {
            handleApiError(error)
        } finally {
            setLoadingPage(false)
        }
    }, [payload.ID, pageRatingQuota, filterNameRatingQuota, getRatingQuota])

    const deleteCalibrationData = useCallback(async (id) => {
        try {
            setLoadingPage(true)
            await axiosInstance.delete(`/auth/calibrations/${id}`)
            toast.success("Success Delete Calibration Data")
            setTimeout(() => window.location.reload(), 1000)
        } catch (error) {
            handleApiError(error)
        } finally {
            setLoadingPage(false)
        }
    }, [])

    return {
        payload,
        getProjectForm,
        ratingQuotas,
        getRatingQuota,
        users,
        getUsersData,
        pageRatingQuota,
        totalPageRatingQuota,
        changePageRatingQuota,
        pageUsers,
        totalPageUsers,
        changePageUsers,
        publishProject,
        deactivateProject,
        setDataCalibration,
        userCalibration,
        notifyCalibrator,
        deleteRatingQuota,
        deleteCalibrationData,
        loadingPagestate,
        setFilterNameUserRatingQuota,
        limitRatingQuota,
        filterNameRatingQuota,
        setFilterLimitRatingQuota,
        setFilterNameUsers,
        limitUsers,
        filterName,
        setFilterLimitUsers,
        divisions,
        getDivisions,
        filterDivision,
        setFilterDivisionUsers,
    }
}

export default useProjectDetail